/**
 * @desc js 小盾风控接入
 * @tdStorageTime 有效值缓存时间

 * 实际页面取值处理
 * localStorage.getItem('tongdunBlackBox') || '';
 */
import {
  IsKEWLApp
} from './uaInfo';
import { loadJs } from './utility';
import {
  cache as blackBoxCache,
  validBlackBoxMaxLength, degradedBlackBoxMaxLength,
  isValidBlackBox, readBlackBox, saveBlackBox, clearBlackBox
} from './tongdunCache';

export { isValidBlackBox } from './tongdunCache';

// 全局挂载
if (!window.TONGDun) {
  window.TONGDun = {};
}
if (!window.TONGDun.time) {
  window.TONGDun.time = {};
}

/** 保留 tongdun.js 已挂载的 clear，用于清理时同步重置底层状态。 */
const originClear = window.TONGDun.clear;
/** 公共同盾脚本地址，autoLoad 只在缺少 tongdun.js 时补加载。 */
const tongdunScriptSrc = '/app/js/dist/tongdun.js?v=202610120001';
/** black_box 轮询间隔，控制等待期间读取缓存的频率。 */
const waitBlackBoxInterval = 50;
/** 直接调用 waitBlackBox 时的默认上限；HTTP 显式等待由调用方传入时长。 */
const defaultWaitBlackBoxTimeout = 1000;
/** 等待超时后的冷却时间，避免连续请求重复创建等待定时器。 */
const waitBlackBoxCooldown = 30 * 1000;
/** 自动加载 Promise 缓存，避免并发触发多次 getUserInfo 或脚本加载。 */
let autoLoadPromise = null;
/** black_box 等待 Promise 缓存，让并发请求共享同一次等待。 */
let waitBlackBoxPromise = null;
/** 最近一次等待超时的时间，用于判断 30 秒冷却窗口。 */
let lastWaitBlackBoxTimeout = 0;

/**
 * 判断页面是否已经存在 tongdun.js，避免重复加载公共同盾脚本。
 * @returns {boolean} 是否已存在 tongdun.js
 */
const hasTongdunScript = () => {
  for (let index = 0; index < document.scripts.length; index += 1) {
    if (document.scripts[index].src && document.scripts[index].src.indexOf('/tongdun.js') > -1) {
      return true;
    }
  }
  return false;
};

/**
 * 记录最近一次同盾返回的原始长度，避免丢弃超长值后无法排查长度分布。
 * @param {string} blackBox 同盾 black_box
 * @returns {number} 原始长度
 */
const recordRawBlackBoxLength = (blackBox) => {
  const rawLength = typeof blackBox === 'string' ? blackBox.length : 0;
  window.TONGDun.time.rawBlackBoxLength = rawLength;
  return rawLength;
};

/**
 * Promise 返回 black_box 时结束等待，失败时继续交给轮询超时兜底。
 * @param {Promise<string>|undefined} result 同盾初始化结果
 * @param {Function} finish 结束等待方法
 */
const finishWhenReady = (result, finish) => {
  if (result && typeof result.then === 'function') {
    result.then((blackBox) => {
      if (blackBox) {
        finish(blackBox);
      }
    }).catch(() => {});
  }
};

// 拉取tongdun.js
export const loadTongdun = () => {
  if (hasTongdunScript()) {
    return Promise.resolve();
  }
  return loadJs(tongdunScriptSrc);
};
/** 复用存储及内存取值策略；HTTP 层可携带本次读取的诊断对象。 */
export const getBlackBox = readBlackBox;
/**
 * 读取回调值或当前缓存中的有效 black_box。
 * @param {string} blackBox 同盾回调返回的 black_box
 * @param {Object|boolean=} options 取值配置
 * @returns {string} 有效 black_box
 */
const getValidBlackBox = (blackBox, options) => (isValidBlackBox(blackBox, options) ? blackBox : getBlackBox(false, options));
/**
 * 缺少一小时内的有效缓存时补加载 SDK，复用缓存模块的存储异常保护。
 * @returns {void}
 */
export const tryLoadTongdun = () => {
  // 检测是否有tongdun.js，避免重复加载
  if (hasTongdunScript()) {
    return;
  }
  if (!getBlackBox(true)) {
    loadTongdun();
  } else {
    const ableTime = new Date().getTime();
    window.TONGDun.time.ableTime = ableTime;
  }
};
// 清除缓存信息
export const clear = function () {
  window.TONGDun.time.lastReason = clearBlackBox(true);
  window.TONGDun.time.rawBlackBoxLength = 0;
  window.TONGDun.time.ableTime = 0;
  if (originClear && originClear instanceof Function) {
    originClear();
  }
  autoLoadPromise = null;
  waitBlackBoxPromise = null;
  lastWaitBlackBoxTimeout = 0;
};

/**
 * 记录 App 采集及存储结果，让独立 SDK 已加载时也能读到当前原因。
 * @param {string} reason App 同盾诊断原因
 */
const markAppReason = (reason) => {
  window.TONGDun.time.lastReason = reason;
  // eslint-disable-next-line no-underscore-dangle
  const state = window.TONGDun._state;
  if (state) {
    state.lastReason = reason;
    state.sdkReason = reason;
  }
};

export const initAutoLoad = () => {
  if (autoLoadPromise) {
    return autoLoadPromise;
  }
  const { generation } = blackBoxCache;
  // initTime只初始化一遍
  !window.TONGDun.time.initTime && (window.TONGDun.time.initTime = new Date().getTime());
  // 自动加载协调本身不产生 SDK 数据，保留实际采集轮次的 raw 长度。
  autoLoadPromise = new Promise((resolve) => {
    // app内通过getUserInfo获取td_black_box
    if (IsKEWLApp && window.KEWLApp && window.KEWLApp.getUserInfo) {
      window.KEWLApp.getUserInfo((data) => {
        // clear 后返回的旧 App 数据不能重新写入存储或内存。
        if (generation !== blackBoxCache.generation) {
          resolve('');
          return;
        }
        try {
          const userInfo = typeof data === 'string' && data !== 'null' && data !== '' ? JSON.parse(data) : data;
          if (userInfo instanceof Object && userInfo.td_black_box) {
            const blackBox = typeof userInfo.td_black_box === 'string' ? userInfo.td_black_box : '';
            recordRawBlackBoxLength(blackBox);
            if (blackBox.length > degradedBlackBoxMaxLength) {
              markAppReason('td_box_too_long');
              clearBlackBox();
              resolve('');
              return;
            }
            window.TONGDun.time.ableTime = new Date().getTime();
            const storageReason = saveBlackBox(blackBox, window.TONGDun.time.ableTime);
            let sdkReason = 'td_empty_callback';
            if (blackBox) {
              sdkReason = blackBox.length <= validBlackBoxMaxLength ? 'td_init_success' : 'td_init_degraded_success';
            }
            markAppReason(storageReason || sdkReason);
            resolve(getBlackBox());
            return;
          }
        } catch (error) {
          console.log('tongdun getUserInfo error', error);
        }
        tryLoadTongdun();
        resolve(getBlackBox());
      });
    } else {
      tryLoadTongdun();
      resolve(getBlackBox());
    }
  }).then((blackBox) => {
    if (!blackBox && generation === blackBoxCache.generation) {
      autoLoadPromise = null;
    }
    return blackBox;
  });
  return autoLoadPromise;
};

/**
 * 等待同盾 black_box 就绪，超时后返回当前快照，避免阻塞业务请求。
 * @param {number} timeout 等待超时时间，单位毫秒
 * @returns {Promise<string>} 同盾 black_box
 */
export const waitBlackBox = (timeout = defaultWaitBlackBoxTimeout) => {
  const storageBlackBox = getBlackBox();
  if (storageBlackBox) {
    lastWaitBlackBoxTimeout = 0;
    return Promise.resolve(storageBlackBox);
  }

  if (
    lastWaitBlackBoxTimeout
    && new Date().getTime() - lastWaitBlackBoxTimeout < waitBlackBoxCooldown
  ) {
    return Promise.resolve(getBlackBox());
  }

  if (waitBlackBoxPromise) {
    return waitBlackBoxPromise;
  }

  waitBlackBoxPromise = new Promise((resolve) => {
    let isFinished = false;
    let timer;
    const startTime = new Date().getTime();
    const finish = (blackBox) => {
      if (isFinished) {
        return;
      }
      isFinished = true;
      clearInterval(timer);
      waitBlackBoxPromise = null;
      const finalBlackBox = getValidBlackBox(blackBox);
      lastWaitBlackBoxTimeout = finalBlackBox ? 0 : new Date().getTime();
      resolve(finalBlackBox);
    };
    timer = setInterval(() => {
      const currentBlackBox = getBlackBox();
      if (currentBlackBox || new Date().getTime() - startTime >= timeout) {
        finish(currentBlackBox);
      }
    }, waitBlackBoxInterval);

    finishWhenReady(initAutoLoad(), finish);

    if (window.TONGDun && typeof window.TONGDun.init === 'function') {
      finishWhenReady(window.TONGDun.init(finish), finish);
    }
  });
  return waitBlackBoxPromise;
};
window.TONGDun.initAutoLoad = initAutoLoad;
window.TONGDun.loadTongdun = loadTongdun;
window.TONGDun.tryLoadTongdun = tryLoadTongdun;
window.TONGDun.clear = clear;
window.TONGDun.getBlackBox = getBlackBox;
window.TONGDun.waitBlackBox = waitBlackBox;
initAutoLoad();
